import React, { useState } from "react";
import { ToggleSwitch } from "../common/RouteRow";
import { getBusColor } from "../../utils/busColors";
import { BusIcon, ChevronIcon, DriverIcon, LineIcon, LocateIcon } from "./treeIcons";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function LineGroup({
    line, lineKey, hiddenRoutes, expandedKeys, toggleExpanded,
    toggleRoutes, openBusDetails, focusOnLocation
}) {
    const { surName, routeIds, activeRoutes, inactiveRoutes, activeRoutesCount, activeBusesCount, totalRoutes } = line;
    const [isToggling, setIsToggling] = useState(false);

    const isExpanded = expandedKeys.has(lineKey);
    const isLineVisible = routeIds.some(id => !hiddenRoutes.has(id));

    // Prende/apaga todas las rutas de la linea. Prender carga las paradas ruta por ruta, asi que
    // se bloquea el switch mientras tanto para no encolar clics.
    const handleLineToggle = async () => {
        if (isToggling) return;
        setIsToggling(true);
        try {
            await toggleRoutes(routeIds);
        } finally {
            setIsToggling(false);
        }
    };

    const renderBus = (bus) => {
        const plate = bus.busRegistrationId || bus.busName || `#${bus.busId}`;
        return (
            <li key={bus.busId} className="mon-bus">
                <button type="button" className="mon-row-toggle" onClick={() => openBusDetails(bus.busId)} title="Ver detalles de la unidad">
                    <span className="mon-bus-avatar" style={{ backgroundColor: getBusColor(bus.busId) }}>
                        <BusIcon size={14} />
                    </span>
                    <span className="mon-text">
                        <span className="mon-bus-plate">{plate}</span>
                        <span className="mon-bus-driver">
                            <DriverIcon size={11} />
                            <span>{bus.driverName || "En servicio"}</span>
                        </span>
                    </span>
                </button>
                <button
                    type="button"
                    className="mon-bus-locate"
                    onClick={() => focusOnLocation(bus.lastLat, bus.lastLon)}
                    title="Ubicar en mapa"
                    aria-label={`Ubicar bus ${plate} en el mapa`}
                >
                    <LocateIcon size={16} />
                </button>
            </li>
        );
    };

    const renderRoute = (group, isActive) => {
        const routeKey = `${lineKey}:${group.rouName}`;
        const hasBuses = isActive && group.activeBuses.length > 0;
        const isRouteExpanded = hasBuses && expandedKeys.has(routeKey);
        const isGroupVisible = group.routeIds.some(id => !hiddenRoutes.has(id));

        return (
            <div key={group.rouName} className={`mon-route${isGroupVisible ? "" : " is-off"}`}>
                <div className="mon-row">
                    <button
                        type="button"
                        className="mon-row-toggle"
                        onClick={() => toggleExpanded(routeKey)}
                        disabled={!hasBuses}
                        aria-expanded={hasBuses ? isRouteExpanded : undefined}
                    >
                        {hasBuses ? <ChevronIcon isOpen={isRouteExpanded} /> : <span className="mon-route-dot" />}
                        <span className="mon-route-name">{group.rouName}</span>
                        {hasBuses && (
                            <span className="mon-route-count" title={plural(group.activeBuses.length, "bus en servicio", "buses en servicio")}>
                                <BusIcon size={11} />
                                {group.activeBuses.length}
                            </span>
                        )}
                    </button>
                    <ToggleSwitch
                        isOn={isGroupVisible}
                        onToggle={() => toggleRoutes(group.routeIds)}
                        label={`Mostrar ruta ${group.rouName} en el mapa`}
                    />
                </div>
                {isRouteExpanded && (
                    <ul className="mon-buses">
                        {group.activeBuses.map(renderBus)}
                    </ul>
                )}
            </div>
        );
    };

    return (
        <div className={`mon-line${activeBusesCount > 0 ? " is-live" : ""}${isLineVisible ? "" : " is-off"}`}>
            <div className="mon-row">
                <button type="button" className="mon-row-toggle" onClick={() => toggleExpanded(lineKey)} aria-expanded={isExpanded}>
                    <ChevronIcon isOpen={isExpanded} />
                    <span className="mon-line-icon"><LineIcon size={15} /></span>
                    <span className="mon-text">
                        <span className="mon-line-name">{surName}</span>
                        <span className="mon-line-meta">
                            {activeBusesCount > 0
                                ? <><b>{plural(activeBusesCount, "bus", "buses")}</b> · {activeRoutesCount}/{totalRoutes} rutas activas</>
                                : <>Sin buses · {plural(totalRoutes, "ruta", "rutas")}</>}
                        </span>
                    </span>
                </button>
                <ToggleSwitch
                    isOn={isLineVisible}
                    onToggle={handleLineToggle}
                    isBusy={isToggling}
                    label={`Mostrar todas las rutas de ${surName} en el mapa`}
                />
            </div>

            {isExpanded && (
                <div className="mon-branch">
                    {activeRoutes.length > 0 && (
                        <>
                            <div className="mon-section is-live">
                                En servicio <span className="mon-section-count">{activeRoutes.length}</span>
                            </div>
                            {activeRoutes.map(group => renderRoute(group, true))}
                        </>
                    )}
                    {inactiveRoutes.length > 0 && (
                        <>
                            <div className="mon-section">
                                Sin servicio <span className="mon-section-count">{inactiveRoutes.length}</span>
                            </div>
                            {inactiveRoutes.map(group => renderRoute(group, false))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
